import { useEffect, useRef, useState } from "react";
import { Calendar as CalendarIcon } from "lucide-react";
import { cn } from "../../utils/cn";
import { FLOW_TEXT_INPUT_BASE } from "../../styles/twDesignPrimitives";
import {
  Calendar,
  formatDisplayDate,
  formatIsoDate,
  parseIsoDate,
} from "./Calendar";

type Props = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
};

export function DatePicker({
  label,
  value,
  onChange,
  placeholder = "Selecionar data",
  className = "",
}: Props) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const selectedDate = parseIsoDate(value);
  const [visibleMonth, setVisibleMonth] = useState<Date>(
    selectedDate ?? new Date()
  );

  useEffect(() => {
    if (selectedDate) setVisibleMonth(selectedDate);
  }, [value]);

  useEffect(() => {
    if (!open) return;

    function onDocMouseDown(event: MouseEvent) {
      const root = rootRef.current;
      if (!root || root.contains(event.target as Node)) return;
      setOpen(false);
    }

    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open]);

  function handleSelect(date: Date) {
    onChange(formatIsoDate(date));
    setOpen(false);
  }

  const displayValue = formatDisplayDate(value);

  return (
    <div ref={rootRef} className={cn("relative min-w-[160px] shrink-0", className)}>
      <p className="mb-1 block w-full text-left text-xs font-medium text-slate-600">
        {label}
      </p>

      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className={cn(
          FLOW_TEXT_INPUT_BASE,
          "flex items-center justify-between gap-2 !text-left !font-[inherit]",
          open && "border-[#3182CE] ring-2 ring-[#3182CE]/30"
        )}
      >
        <span className={cn("min-w-0 flex-1 truncate", !displayValue && "text-[#A0AEC0]")}>
          {displayValue || placeholder}
        </span>
        <CalendarIcon className="h-4 w-4 shrink-0 text-slate-500" />
      </button>

      {open ? (
        <div
          role="dialog"
          aria-label={`Calendário — ${label}`}
          className="absolute left-0 top-full z-50 mt-1 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg"
        >
          <Calendar
            selected={selectedDate}
            month={visibleMonth}
            onMonthChange={setVisibleMonth}
            onSelect={handleSelect}
          />
        </div>
      ) : null}
    </div>
  );
}
